import dynamic from 'next/dynamic'
import { useContext } from 'react'

import { FederatedComponentBoundary } from '@fe-toolkit/web-components/ssr'

import { SubMenu } from '@/components/organisms/SubMenu'

import FallbackFooter from '@/components/fallbacks/FallbackFooter'
import FallbackHeader from '@/components/fallbacks/FallbackHeader'

import { BrandRefreshContext } from '@/utils/brandRefresh/brandRefresh'

import { PageHead } from '../PageHead'

import { useChromeFlags } from './ChromeFlagsContext'

import type { ReactNode } from 'react'

// Federated Header/Footer render client-only. The whole provider tree sits
// under HydrationGuard, which renders nothing on the server, so no page content
// is server-rendered; `ssr: false` matches that and keeps the server off the
// federation path. The shared FallbackHeader/FallbackFooter paint while the
// chunk loads; the federated version swaps in on the client.
//
// The same fallbacks are also registered in the mfOfflineHandlingPlugin
// errorLoadRemote registry (see next.config.mjs) so a DOWN remote resolves to
// them instead of next/dynamic swallowing the rejection and rendering null.
const CommonHeader = dynamic(() => import('fe_common/components/CommonHeader'), {
  ssr: false,
  loading: () => <FallbackHeader />,
})

const CommonFooter = dynamic(() => import('fe_common/components/CommonFooter'), {
  ssr: false,
  loading: () => <FallbackFooter />,
})

// Server-rendered, but only downloaded (with Tamagui) for visitors in the brand refresh.
const BrandRefreshLayout = dynamic(() => import('@/layouts/BrandRefresh'))

export default function MainLayout({
  children,
  title = `we're up to good`,
  description = 'Doing good is our thing. Our super SIM plans, stonking refurbished phone deals plus Fixed UK prices for 2023 are all good news for your wallet. Find out more',
  username,
}: Readonly<{
  children?: ReactNode
  title?: string
  description?: string
  username?: string
}>) {
  // The server render's decision (fe-common's, else this MFE's own). The Footer has no pre-registration variant.
  const chromeFlags = useChromeFlags()
  const footerFlags = {
    isBrandRefresh: chromeFlags.isBrandRefresh,
    isBlackFriday: chromeFlags.isBlackFriday,
  }
  const isBrandRefresh = useContext(BrandRefreshContext)

  if (isBrandRefresh) {
    return (
      <>
        <PageHead description={description} title={title} />
        <BrandRefreshLayout username={username}>
          <SubMenu />
          {/* tabIndex lets the header's skip link move focus here */}
          <main id="main-content" tabIndex={-1}>
            {children}
          </main>
        </BrandRefreshLayout>
      </>
    )
  }

  return (
    <>
      <PageHead description={description} title={title} />
      <FederatedComponentBoundary fallback={<FallbackHeader username={username} />} logLabel="fe_common Header">
        <CommonHeader username={username} {...chromeFlags} />
      </FederatedComponentBoundary>
      <SubMenu />
      <main id="main-content">{children}</main>
      <FederatedComponentBoundary fallback={<FallbackFooter />} logLabel="fe_common Footer">
        <CommonFooter {...footerFlags} />
      </FederatedComponentBoundary>
    </>
  )
}
